import { EVALUATION_SCENE, EVALUATION_PERMISSION_TYPE } from './constant';
import type {
  EditEvaluationPermission,
  DimensionData,
  ScholarshipQuestionData,
} from './types';
import { SimplifyRoles } from '../../../../../utils';

export const evaluationPermission = (
  role: SimplifyRoles,
  scene: EVALUATION_SCENE,
  hasHKPFS: boolean,
  hasUPAFWithoutHKPFS: boolean,
  academicExcellenceQuestions: ScholarshipQuestionData[],
  researchPotentialQuestions: ScholarshipQuestionData[],
  strengthsQuestions: ScholarshipQuestionData[],
  distinctiveQualityQuestions: ScholarshipQuestionData[],
  HKPFSDimension: DimensionData[],
  UPAFDimension: DimensionData[],
): EditEvaluationPermission => {
  const commonStaff = Object.values(SimplifyRoles).filter(
    role =>
      role !== SimplifyRoles.SUPERVISOR &&
      role !== SimplifyRoles.INTERVIEW_PANEL &&
      role !== SimplifyRoles.DORPGS,
  );

  const disableHKPFSDimension = HKPFSDimension.map(() => false);
  const disableUPAFDimension = UPAFDimension.map(() => false);
  const HKPFSDimensionEditable = HKPFSDimension.map(
    dimension => dimension.editableRoles?.includes(role) ?? false,
  );
  const UPAFDimensionEditable = UPAFDimension.map(
    dimension => dimension.editableRoles?.includes(role) ?? false,
  );

  const defaultEvaluation: EditEvaluationPermission = {
    academicPermit: EVALUATION_PERMISSION_TYPE.VIEW,
    researchPermit: EVALUATION_PERMISSION_TYPE.VIEW,
    strengthPermit: EVALUATION_PERMISSION_TYPE.VIEW,
    distinctiveQualityPermit: EVALUATION_PERMISSION_TYPE.VIEW,

    calculateAllScoresPermit: EVALUATION_PERMISSION_TYPE.HIDE,
    endorsedPermit: EVALUATION_PERMISSION_TYPE.VIEW,
    academicProgrammePermit: EVALUATION_PERMISSION_TYPE.VIEW,

    researchAssessEditable: false,
    interviewAssessEditable: false,

    HKPFSDimensionVisible: hasHKPFS,
    UPAFDimensionVisible: hasUPAFWithoutHKPFS,
    HKPFSDimensionEditable: disableHKPFSDimension,
    UPAFDimensionEditable: disableUPAFDimension,

    displayNotification: false,
    panelMeetingRemarksPermit: false,
  };

  const polishCommentsPermission: EditEvaluationPermission = {
    academicPermit: EVALUATION_PERMISSION_TYPE.EDIT,
    researchPermit: EVALUATION_PERMISSION_TYPE.EDIT,
    researchAssessEditable: false,
    strengthPermit: EVALUATION_PERMISSION_TYPE.EDIT,
    distinctiveQualityPermit: EVALUATION_PERMISSION_TYPE.EDIT,
    calculateAllScoresPermit: EVALUATION_PERMISSION_TYPE.HIDE,
    interviewAssessEditable: false,
    endorsedPermit: EVALUATION_PERMISSION_TYPE.HIDE,
    academicProgrammePermit: EVALUATION_PERMISSION_TYPE.HIDE,
    displayNotification: false,
    panelMeetingRemarksPermit: true,
    HKPFSDimensionVisible: hasHKPFS,
    UPAFDimensionVisible: hasUPAFWithoutHKPFS,
    HKPFSDimensionEditable: disableHKPFSDimension,
    UPAFDimensionEditable: disableUPAFDimension,
  };

  const defaultEvaluationPermission: Record<
    EVALUATION_SCENE,
    EditEvaluationPermission
  > = {
    [EVALUATION_SCENE.NOMINATION]: defaultEvaluation,
    [EVALUATION_SCENE.INTERVIEW]: defaultEvaluation,
    [EVALUATION_SCENE.ENDORSEMENT]: defaultEvaluation,
    [EVALUATION_SCENE.POLISH_COMMENTS]: defaultEvaluation,
    [EVALUATION_SCENE.VIEW_ONLY]: defaultEvaluation,
    [EVALUATION_SCENE.DORPG_PREVIEW]: defaultEvaluation,
  };

  const evaluationPermissionMap: Partial<
    Record<SimplifyRoles, Record<EVALUATION_SCENE, EditEvaluationPermission>>
  > = {
    [SimplifyRoles.SUPERVISOR]: {
      ...defaultEvaluationPermission,
      [EVALUATION_SCENE.NOMINATION]: {
        academicPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchAssessEditable: true,
        strengthPermit: EVALUATION_PERMISSION_TYPE.HIDE,
        distinctiveQualityPermit: EVALUATION_PERMISSION_TYPE.HIDE,
        calculateAllScoresPermit: [
          academicExcellenceQuestions,
          researchPotentialQuestions,
        ].every(questions =>
          questions.every(question => question.comment.length > 0),
        )
          ? EVALUATION_PERMISSION_TYPE.EDIT
          : EVALUATION_PERMISSION_TYPE.VIEW,
        interviewAssessEditable: false,
        endorsedPermit: EVALUATION_PERMISSION_TYPE.HIDE,
        academicProgrammePermit: EVALUATION_PERMISSION_TYPE.HIDE,
        displayNotification: true,
        panelMeetingRemarksPermit: false,
        HKPFSDimensionVisible: hasHKPFS,
        UPAFDimensionVisible: hasUPAFWithoutHKPFS,
        HKPFSDimensionEditable,
        UPAFDimensionEditable,
      },
      [EVALUATION_SCENE.POLISH_COMMENTS]: polishCommentsPermission,
    },
    [SimplifyRoles.DORPGS]: {
      ...defaultEvaluationPermission,
      [EVALUATION_SCENE.ENDORSEMENT]: {
        // Update at 17/01/2025 after the oral discussion with Harris, to allow dorpg to edit everything
        academicPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchAssessEditable: true,
        strengthPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        distinctiveQualityPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        calculateAllScoresPermit: [
          academicExcellenceQuestions,
          researchPotentialQuestions,
          strengthsQuestions,
          distinctiveQualityQuestions,
        ].every(questions =>
          questions.every(question => question.comment.length > 0),
        )
          ? EVALUATION_PERMISSION_TYPE.EDIT
          : EVALUATION_PERMISSION_TYPE.VIEW,
        interviewAssessEditable: true,
        panelMeetingRemarksPermit: false,
        HKPFSDimensionVisible: hasHKPFS,
        UPAFDimensionVisible: hasUPAFWithoutHKPFS,
        HKPFSDimensionEditable: HKPFSDimension.map(() => true),
        UPAFDimensionEditable: UPAFDimension.map(() => true),
        endorsedPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        displayNotification: true,
        academicProgrammePermit: EVALUATION_PERMISSION_TYPE.EDIT,
      },
      [EVALUATION_SCENE.POLISH_COMMENTS]: polishCommentsPermission,
      [EVALUATION_SCENE.DORPG_PREVIEW]: {
        ...defaultEvaluation,
        endorsedPermit: EVALUATION_PERMISSION_TYPE.HIDE,
        academicProgrammePermit: EVALUATION_PERMISSION_TYPE.HIDE,
      },
    },
    [SimplifyRoles.INTERVIEW_PANEL]: {
      ...defaultEvaluationPermission,
      [EVALUATION_SCENE.INTERVIEW]: {
        academicPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        researchAssessEditable: true,
        strengthPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        distinctiveQualityPermit: EVALUATION_PERMISSION_TYPE.EDIT,
        calculateAllScoresPermit: [
          academicExcellenceQuestions,
          researchPotentialQuestions,
          strengthsQuestions,
          distinctiveQualityQuestions,
        ].every(questions =>
          questions.every(question => question.comment.length > 0),
        )
          ? EVALUATION_PERMISSION_TYPE.EDIT
          : EVALUATION_PERMISSION_TYPE.VIEW,
        interviewAssessEditable: true,
        endorsedPermit: EVALUATION_PERMISSION_TYPE.HIDE,
        academicProgrammePermit: EVALUATION_PERMISSION_TYPE.HIDE,
        displayNotification: true,
        panelMeetingRemarksPermit: false,
        HKPFSDimensionVisible: hasHKPFS,
        UPAFDimensionVisible: hasUPAFWithoutHKPFS,
        HKPFSDimensionEditable,
        UPAFDimensionEditable,
      },
    },
    ...commonStaff.reduce(
      (acc, role) => ({
        ...acc,
        [role]: defaultEvaluationPermission,
      }),
      {},
    ),
  };

  return (
    evaluationPermissionMap?.[role]?.[scene] ??
    defaultEvaluationPermission[scene]
  );
};
